import { SortingState } from '@tanstack/react-table';
import {
  isNotSelectAll,
  isValidOption,
} from 'components/forms/multiSelectField/MultiSelectField.constants';
import { SortDirection } from 'components/table/Table.types';
import { ExpectedQueryData } from 'components/table/hooks/useServerFilteredTable';
import { getPaginationParams } from 'components/table/utils/pagination';
import { getSortParam } from 'components/table/utils/sorting';
import { GetTriggersResponseTO, Trigger } from 'models/automation/Triggers';
import { getRecipientUidAndTypeFromValue } from 'pages/automations/triggers/TriggersTab.utils';
import { TriggersTabFiltersSchemaType } from 'pages/automations/triggers/filters/TriggersTabFilters.schema';
import API from 'services/API';

type QueryReturnType = ExpectedQueryData<Trigger>;

export const TRIGGERS_BASE_QUERY_KEY = 'triggers';

export const TRIGGERS_SORTING = {
  name: 'SORT_BY_NAME',
  actionType: 'SORT_BY_ACTION_TYPE',
  eventType: 'SORT_BY_EVENT_TYPE',
  triggerType: 'SORT_BY_TRIGGER_TYPE',
  templateName: 'SORT_BY_TEMPLATE_NAME',
  createdDate: 'SORT_BY_CREATED_DATE',
};

export const fetchAllTriggers = async (queryParams: {
  _limit?: number;
  _offset?: number | undefined;
  actionTypes?: string[];
  channels?: string[];
  eventTypes?: string[];
  properties?: string[];
  recipientUids?: string[];
  recipientTypes?: string[];
  templateUids?: string[];
  triggerName?: string;
  sort?: string;
  sortDirection?: SortDirection;
}) => {
  const stringifyArrayParam = (param: keyof typeof queryParams) => {
    const field = queryParams[param];

    return Array.isArray(field) && field.length
      ? { [param]: field.join(',') }
      : {};
  };
  const response = await API.get<GetTriggersResponseTO>(
    '/api/internal/triggers',
    {
      params: {
        ...queryParams,
        ...stringifyArrayParam('actionTypes'),
        ...stringifyArrayParam('channels'),
        ...stringifyArrayParam('eventTypes'),
        ...stringifyArrayParam('properties'),
        ...stringifyArrayParam('recipientUids'),
        ...stringifyArrayParam('recipientTypes'),
        ...stringifyArrayParam('templateUids'),
      },
    },
  );

  return response.data;
};

export const triggersQuery = ({
  filters,
  pageIndex,
  pageSize,
  searchTerm,
  sorting,
}: {
  filters: Partial<TriggersTabFiltersSchemaType>;
  pageIndex: number;
  pageSize: number;
  searchTerm?: string;
  sorting: SortingState;
}) => ({
  queryKey: [
    TRIGGERS_BASE_QUERY_KEY,
    'list',
    { filters, pageIndex, searchTerm, sorting },
  ],
  queryFn: async (): Promise<QueryReturnType> => {
    const sort = getSortParam(sorting, TRIGGERS_SORTING);

    const data = await fetchAllTriggers({
      actionTypes: filters.actionTypes
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      channels: filters.channels
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      eventTypes: filters.eventTypes
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      properties: filters.properties
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      recipientUids: filters.recipients
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .flatMap((option) => {
          const { recipientUid } = getRecipientUidAndTypeFromValue(
            option.value,
          );
          return recipientUid ? [recipientUid] : [];
        }),
      recipientTypes: filters.recipients
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => getRecipientUidAndTypeFromValue(option.value).type),
      templateUids: filters.templateUids
        ?.filter(isValidOption)
        ?.filter(isNotSelectAll)
        .map((option) => option.value),
      ...(searchTerm ? { triggerName: searchTerm } : {}),
      ...getPaginationParams({ pageIndex, pageSize }),
      ...(sort ? { sort: sort.sort, sortDirection: sort.direction } : {}),
    });

    const { triggers, _metadata } = data;

    return {
      data: triggers,
      _metadata,
    };
  },
  enabled: true,
});
